import { getQueryParameterFromUri } from "@WhiteCap/Common/Utillities/utils";
import {
    SubscribedEmailDto,
    SubscriptionsDashboardState,
} from "@WhiteCap/Store/Reducer/WhiteCapReducer/OsnSubscriptionsDashboard/SubscriptionsDashboard";

import { ApiResultModel as SoaApiResultModel } from "@WhiteCap/Store/Service/SoaSubscribersService";
import { Draft } from "immer";

const soaSubscriptions = {
    "Custom/SubscriptionDashboard/BeginSoaSubscribersDataLoad": (draft: Draft<SubscriptionsDashboardState>) => {
        draft.isFetchingSubscribers = true;
    },
    "Custom/SubscriptionDashboard/SoaSubscriptions/SoaSubscribersDataLoadError": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        // Error is being handled from handler itself
        draft.isFetchingSubscribers = false;
    },
    "Custom/SubscriptionDashboard/CompleteSoaSubscribersDataLoad": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: SoaApiResultModel;
        },
    ) => {
        const customerId = getQueryParameterFromUri(action.result?.uri, "customerId");
        const data = {
            properties: action.result.properties,
            subscribedEmails: action.result.subscribedEmails,
            count: action.result.soaOrderStatusSummary?.count,
            isPrintPriceEnabled: action.result.soaOrderStatusSummary?.isPrintPriceEnabled,
        };
        if (draft.osnType === "accountLevel") {
            draft.accountLevelSubscriptions.soaOrderStatusSummary = data;
        } else if (draft.osnType === "individualJobSiteLevel" && customerId) {
            draft.jobSiteSubscriptionsById[customerId].soaOrderStatusSummary = data;
        }
        draft.isFetchingSubscribers = false;
    },
    "Custom/SubscriptionDashboard/SoaSubscriptions/CompleteRemoveSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            value: string;
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] =
                draft.accountLevelSubscriptions.soaOrderStatusSummary?.subscribedEmails || [];
            const updatedEmails = emails.filter(email => email.email !== action.value);
            draft.accountLevelSubscriptions.soaOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.soaOrderStatusSummary,
                subscribedEmails: updatedEmails,
                count: (draft.accountLevelSubscriptions.soaOrderStatusSummary?.count || 0) - 1,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary
                    ?.subscribedEmails || [];
            const updatedEmails = emails.filter(email => email.email !== action.value);
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary,
                subscribedEmails: updatedEmails,
                count:
                    (draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary?.count ||
                        0) - 1,
            };
        }
    },
    "Custom/SubscriptionDashboard/SoaSubscriptions/CompleteAddSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: SubscribedEmailDto;
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] =
                draft.accountLevelSubscriptions.soaOrderStatusSummary?.subscribedEmails || [];
            emails.unshift(action.result);
            draft.accountLevelSubscriptions.soaOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.soaOrderStatusSummary,
                subscribedEmails: emails,
                count: (draft.accountLevelSubscriptions.soaOrderStatusSummary?.count || 0) + 1,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary
                    ?.subscribedEmails || [];
            emails.unshift(action.result);
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary,
                subscribedEmails: emails,
                count:
                    (draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary?.count ||
                        0) + 1,
            };
        }
    },
    "Custom/SubscriptionDashboard/SoaSubscriptions/CompleteUpdatingSubscriber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: {
                customerId: string;
                subscribedEmailDto: SubscribedEmailDto;
            };
        },
    ) => {
        const updatedEmailId = action.result.subscribedEmailDto.email;
        const getUpdatedEmailDtos = (updatedId: string, emailsDto: SubscribedEmailDto[]) => {
            const updatedSubscribedEmails = emailsDto?.map(emailDto => {
                let updatedEmailDto: SubscribedEmailDto = emailDto;
                if (emailDto.email === updatedEmailId) {
                    updatedEmailDto = { ...action.result.subscribedEmailDto };
                }
                return updatedEmailDto;
            });
            return updatedSubscribedEmails;
        };
        if (draft.osnType === "accountLevel") {
            const updatedSubscribedEmails = getUpdatedEmailDtos(
                updatedEmailId,
                draft.accountLevelSubscriptions.soaOrderStatusSummary?.subscribedEmails || [],
            );
            draft.accountLevelSubscriptions.soaOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.soaOrderStatusSummary,
                subscribedEmails: updatedSubscribedEmails,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const updatedSubscribedEmails = getUpdatedEmailDtos(
                updatedEmailId,
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary
                    ?.subscribedEmails || [],
            );
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary,
                subscribedEmails: updatedSubscribedEmails,
            };
        }
    },

    "Custom/SubscriptionDashboard/SoaSubscriptions/CompleteRemovePhoneNumber": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            result: {
                emailId: string;
            };
        },
    ) => {
        if (draft.osnType === "accountLevel") {
            const emails: SubscribedEmailDto[] =
                draft.accountLevelSubscriptions.soaOrderStatusSummary?.subscribedEmails || [];
            const updatedEmails = emails.map(email => ({
                ...email,
                phone: email.email === action.result.emailId ? "" : email.phone,
                isSubscribedToReceiveSMS:
                    email.email === action.result.emailId ? false : email.isSubscribedToReceiveSMS,
            }));
            draft.accountLevelSubscriptions.soaOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.soaOrderStatusSummary,
                subscribedEmails: updatedEmails,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            const emails: SubscribedEmailDto[] =
                draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary
                    ?.subscribedEmails || [];
            const updatedEmails = emails.map(email => ({
                ...email,
                phone: email.email === action.result.emailId ? "" : email.phone,
                isSubscribedToReceiveSMS:
                    email.email === action.result.emailId ? false : email.isSubscribedToReceiveSMS,
            }));
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary,
                subscribedEmails: updatedEmails,
            };
        }
    },
    "Custom/SubscriptionDashboard/SoaSubscriptions/BeginApplySoaSubscriptions": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = true;
    },
    "Custom/SubscriptionDashboard/SoaSubscriptions/ApplySoaSubscriptionsError": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = false;
    },
    "Custom/SubscriptionDashboard/SoaSubscriptions/CompleteApplySoaSubscriptions": (
        draft: Draft<SubscriptionsDashboardState>,
    ) => {
        draft.isApplyingSubscriptionsChanges = false;
    },
    "Custom/SubscriptionDashboard/SoaSubscriptions/UpdatePrintPrice": (
        draft: Draft<SubscriptionsDashboardState>,
        action: {
            printPrice: boolean,
        }
    ) => {
        if (draft.osnType === "accountLevel") {
            draft.accountLevelSubscriptions.soaOrderStatusSummary = {
                ...draft.accountLevelSubscriptions.soaOrderStatusSummary,
                isPrintPriceEnabled: action.printPrice,
            };
        } else if (draft.osnType === "individualJobSiteLevel") {
            draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary = {
                ...draft.jobSiteSubscriptionsById[draft.currentSubscriptionCustomerId].soaOrderStatusSummary,
                isPrintPriceEnabled: action.printPrice,
            };
        }
    },
};

export default soaSubscriptions;
